/**
 * posture-brief.ts — Editorial copy layer for the Command Center standfirst.
 *
 * Composes the daily brief (kicker, headline, deck, verdict, byline) from the
 * active posture state. The voice is "trusted advisor": short sentences, no
 * em-dashes, period-separated rather than dash-bridged.
 *
 * Coherence rule: every slot (modifier, verdict, weak-link, deck-trend,
 * deck-action) routes off the same `bucketDelta` direction. Previously the
 * 55-69 band returned "weakening" regardless of delta — so a 65/flat tenant
 * read "Posture is weakening" paired with "Steady-state posture for the past
 * month, with no meaningful shift". The pickers below now consult the bucket
 * at every band; the trend sentence and the modifier can no longer disagree.
 *
 * Each editorial slot has a small set of variants. A variant is picked
 * deterministically from a `${tenantId}:${localDay}:${slot}` seed so the same
 * tenant sees a stable brief through the day, and different days vary.
 *
 * The templates intentionally stay conservative — no Mad Libs, no free-form
 * generation. Word counts on the verdict line stay within a six-word budget
 * so the ScoreBox quote always fits.
 *
 * Risk-slot precedence (single source of truth — see `buildDeck` for detail):
 *   1. topAction      — when the priority-actions ranker has a #1 item.
 *   2. Pillar-laggard — when one pillar is < 70 and stands out.
 *   3. Hygiene / Compound — fallback.
 *
 * The brief narrates the operator queue rather than secretly reordering it:
 * if a class of fix (e.g. low MFA adoption) deserves to lead, that belongs in
 * the ranker so the queue visibly reflects it, not as an editorial override.
 *
 * Loading-state handling: `weakestPillar()` refuses to name a weakest when
 * any pillar input is `undefined`, so the brief never claims "Identity is
 * the laggard" while BPA is still streaming in (or when BPA is null because
 * the tenant has no starred frameworks). The score-level loading guard
 * (`input.score == null`) sits above this and short-circuits to the neutral
 * "Reading posture…" brief.
 */

export type Pillar = 'compliance' | 'bpa' | 'identity'
export type Severity = 'crit' | 'high' | 'med' | 'low' | 'routine'

export interface PostureBrief {
  /** Pill above the headline: "Today's Brief · 29 April" */
  kicker: string
  /** First word(s) of headline rendered roman, e.g. "Posture is" */
  headlineSubject: string
  /** Italic emphasis word, e.g. "strong" — gets `<em>` treatment */
  headlineModifier: string
  /** Second sentence on the headline line. Complete, capitalized, period-terminated. */
  weakLink: string
  /** Single-paragraph deck under the headline. Uses `**bold**` markers. */
  deck: string
  /** Six-words-or-fewer verdict in the score box, italic, in quotes */
  verdict: string
  /** Byline footer: "Brief by Guardian Continuous Assessment · For {user}" */
  byline: string
  /** Score delta vs N days ago, drives the green pill */
  delta: { value: number; days: number }
}

export interface PriorityAction {
  id: string
  rank: number
  title: string
  pillar: Pillar
  severity: Severity
  meta: string
  href: string
  /** Optional external "learn more" URL — typically Microsoft Learn for BPA
   *  rows. Renders as a small icon affordance the user can click to open in
   *  a new tab; the row's primary click target stays on `href` (in-app). */
  learnMoreUrl?: string | null
  /** Computed impact score from the server-side ranking formula. Optional —
   *  exposed for production debugging tooltips, not consumed by default
   *  rendering. See operations-priority-alerts-curation.md §4.4. */
  impact?: number
  /** Number of underlying entities behind this action (users, sites, etc.). */
  scope?: number
  /** Distinct frameworks affected by the underlying policy/drift group. */
  frameworksAffected?: number
  /** Estimated Guardian Score lift in percentage points if this action is closed.
   *  Null for items not policy-mapped (drift, identity templates) or when the
   *  approximation isn't available. Frontend hides the chip on null. */
  guardianScoreDelta?: number | null
}

/* -------------------------------------------------------------------------- */
/* Input shape                                                                */
/* -------------------------------------------------------------------------- */

export interface PostureBriefPillarScores {
  compliance?: number
  bpa?: number
  /** Identity pillar surface — member MFA adoption %. */
  mfa?: number
  /** Aux: M365 Secure Score %. Reserved for future templates. */
  secureScore?: number
}

export interface PostureBriefInput {
  /** Currently signed-in user email, e.g. "adam.jones@iuvotech.com" */
  userEmail?: string
  /**
   * Active tenant ID. Combined with the local day to seed variant selection
   * so the brief is stable per tenant per day. Falls back to a constant when
   * absent (still deterministic, just shared across tenants).
   */
  tenantId?: string
  /** Composite Guardian Score (0-100). */
  score?: number | null
  pillarScores?: PostureBriefPillarScores
  /** 30-day score delta, drives modifier choice and the score-box pill. */
  deltas?: { score?: number; days?: number }
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Open critical-severity issue count — drives the weak-link clause. */
  criticalCount?: number
  /** Override "today" — primarily for testing the kicker. */
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const PILLAR_LABEL: Record<Pillar, string> = {
  compliance: 'Compliance',
  bpa: 'Best Practice',
  identity: 'Identity',
}

/**
 * Single-word pillar names — used inside the six-word verdict cap so
 * "Best Practice" doesn't blow past the budget.
 */
const PILLAR_LABEL_SHORT: Record<Pillar, string> = {
  compliance: 'Compliance',
  bpa: 'Practice',
  identity: 'Identity',
}

/** Format today as "Today's Brief · 29 April" using en-GB long-month. */
function formatKicker(now: Date): string {
  const fmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' })
  return `Today's Brief · ${fmt.format(now)}`
}

/** Local-day key (YYYY-MM-DD) — matches the kicker's local-time framing. */
function localDayKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Five-state delta bucket. Matches the existing TREND_* break-points so the
 * modifier / verdict / action pickers route off the same direction the
 * trend sentence already does. (Mirrors `brief-helpers.bucketDelta` — kept
 * local so posture-brief stays self-contained per the module header note.)
 */
type DeltaBucket = 'risingFast' | 'rising' | 'flat' | 'slipping' | 'falling'

function bucketDelta(deltaValue: number): DeltaBucket {
  if (deltaValue >= 3) return 'risingFast'
  if (deltaValue > 0) return 'rising'
  if (deltaValue === 0) return 'flat'
  if (deltaValue >= -3) return 'slipping'
  return 'falling'
}

function isNegativeBucket(b: DeltaBucket): boolean {
  return b === 'slipping' || b === 'falling'
}

function isNonNegativeBucket(b: DeltaBucket): boolean {
  return b === 'rising' || b === 'risingFast' || b === 'flat'
}

/** djb2 string hash. Stable across runs, no crypto needed for variant picking. */
function hash(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i)
  }
  return h >>> 0
}

/** Pick one variant deterministically from `(seed, slot)`. */
function pickVariant<T>(variants: readonly T[], seed: string, slot: string): T {
  if (variants.length === 0) throw new Error(`pickVariant: empty variants for slot "${slot}"`)
  const idx = hash(`${seed}|${slot}`) % variants.length
  return variants[idx]
}

function normalizePillars(
  raw?: PostureBriefPillarScores,
): { compliance?: number; bpa?: number; identity?: number } {
  if (!raw) return {}
  return {
    compliance: typeof raw.compliance === 'number' ? raw.compliance : undefined,
    bpa: typeof raw.bpa === 'number' ? raw.bpa : undefined,
    identity: typeof raw.mfa === 'number' ? raw.mfa : undefined,
  }
}

/**
 * Returns the lowest-scoring pillar, but ONLY when all three pillar scores
 * are defined. If any pillar is `undefined` (still loading, or a steady-state
 * `null` like "no starred BPA frameworks"), we cannot honestly call any
 * pillar "the weakest" — naming one based on a partial set lies to the user
 * (the historical bug: `bpa` arriving last meant `identity` got named as
 * weakest while BPA was actually lower once it loaded).
 *
 * Returning `undefined` here routes the verdict / weak-link / deck-risk
 * branches into their no-laggard fallback copy, which doesn't name a pillar.
 */
function weakestPillar(
  pillars: { compliance?: number; bpa?: number; identity?: number },
): { pillar: Pillar; score: number } | undefined {
  const order: Pillar[] = ['identity', 'bpa', 'compliance']
  // Bail if any pillar is missing — refuse to name a weakest from partial data.
  for (const p of order) {
    if (typeof pillars[p] !== 'number') return undefined
  }
  let best: { pillar: Pillar; score: number } | undefined
  for (const p of order) {
    const s = pillars[p] as number
    if (!best || s < best.score) best = { pillar: p, score: s }
  }
  return best
}

function imperativeFromAction(title?: string): string {
  if (!title) return 'triage'
  const first = title.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '') ?? ''
  const known = new Set([
    'enforce',
    'roll',
    'reclaim',
    'review',
    'remove',
    'rotate',
    'restrict',
    'require',
    'rebuild',
    'remediate',
    'audit',
    'patch',
    'disable',
    'block',
    'tighten',
    'triage',
    'address',
  ])
  return known.has(first) ? first : 'triage'
}

function cap(s: string): string {
  if (!s) return s
  return s[0].toUpperCase() + s.slice(1)
}

/* -------------------------------------------------------------------------- */
/* Verdict variants (six-words-or-fewer per the ScoreBox cap)                 */
/* -------------------------------------------------------------------------- */

const VERDICT_EXCELLENT: readonly string[] = [
  "Excellent. You're on the right track.",
  'Excellent posture. Keep it steady.',
  'Top form. Routine watch only.',
  'Strong across the board.',
]

const VERDICT_STRONG_NO_LAGGARD: readonly string[] = [
  'Strong overall. One soft spot.',
  'Solid. One small soft spot.',
  'Healthy overall. One thing to watch.',
]

/** Templates take a `{pillar}` token (short pillar name, single word). */
const VERDICT_STRONG_LAGGARD: readonly string[] = [
  'Strong overall. {pillar} needs a hand.',
  'Good shape. Lift {pillar} next.',
  'Healthy. {pillar} is the soft spot.',
]

/** High score (≥85) AND slipping/falling delta. Previously this state fell
 *  through to STRONG / EXCELLENT and read as "Strong across the board" while
 *  the deck trend was saying the score had dropped meaningfully. */
const VERDICT_HIGH_SLIPPING: readonly string[] = [
  'Strong, but softening.',
  'High base, easing back.',
  'Posture is slipping.',
]

/** Templates take a `{pillar}` token. High score with a named laggard AND a
 *  slipping trend — pairs the soft pillar with a slipping-state read. */
const VERDICT_HIGH_SLIPPING_LAGGARD: readonly string[] = [
  'Slipping. Watch {pillar}.',
  '{pillar} is starting to slip.',
  'Softening. {pillar} is the drag.',
]

/** Templates take a `{verb}` token (capitalized imperative). */
const VERDICT_MIXED: readonly string[] = [
  'Mixed. {verb} first.',
  'Mid-stride. {verb} first.',
  'Steady but soft. {verb} now.',
]

/** Templates take a `{pillar}` token (short pillar name). */
const VERDICT_GAPS: readonly string[] = [
  'Several gaps. Start with {pillar}.',
  'Several gaps. Begin with {pillar}.',
  'Soft posture. Start with {pillar}.',
]

/** No-laggard fallback for the gaps band — used when we don't have enough
 *  signal to honestly name a starting pillar. Six words or fewer. */
const VERDICT_GAPS_NO_LAGGARD: readonly string[] = [
  'Several gaps. Start broad.',
  'Soft posture. Start broad.',
  'Plenty to clean up.',
]

function pickVerdict(
  score: number,
  bucket: DeltaBucket,
  weakest: { pillar: Pillar; score: number } | undefined,
  topAction: PostureBriefInput['topAction'] | undefined,
  seed: string,
): string {
  // ≥90 with a non-negative trend stays "excellent"; ≥90 actually slipping
  // routes to HIGH_SLIPPING so the verdict acknowledges the decline.
  if (score >= 90) {
    if (isNegativeBucket(bucket)) {
      if (weakest && weakest.score < 70) {
        const tpl = pickVariant(VERDICT_HIGH_SLIPPING_LAGGARD, seed, 'verdict.highSlippingLaggard')
        return tpl.replace('{pillar}', PILLAR_LABEL_SHORT[weakest.pillar])
      }
      return pickVariant(VERDICT_HIGH_SLIPPING, seed, 'verdict.highSlipping')
    }
    return pickVariant(VERDICT_EXCELLENT, seed, 'verdict.excellent')
  }

  if (score >= 75) {
    if (isNegativeBucket(bucket)) {
      if (weakest && weakest.score < 70) {
        const tpl = pickVariant(VERDICT_HIGH_SLIPPING_LAGGARD, seed, 'verdict.highSlippingLaggard')
        return tpl.replace('{pillar}', PILLAR_LABEL_SHORT[weakest.pillar])
      }
      return pickVariant(VERDICT_HIGH_SLIPPING, seed, 'verdict.highSlipping')
    }
    if (!weakest || weakest.score > 70) {
      return pickVariant(VERDICT_STRONG_NO_LAGGARD, seed, 'verdict.strong')
    }
    const tpl = pickVariant(VERDICT_STRONG_LAGGARD, seed, 'verdict.laggard')
    return tpl.replace('{pillar}', PILLAR_LABEL_SHORT[weakest.pillar])
  }

  if (score >= 60) {
    const verb = cap(imperativeFromAction(topAction?.title))
    const tpl = pickVariant(VERDICT_MIXED, seed, 'verdict.mixed')
    return tpl.replace('{verb}', verb)
  }

  // When weakest is unknown (loading-race or partial data) we cannot name a
  // pillar honestly. Fall back to a no-laggard verdict in the same band
  // ("Soft posture. Triage broadly.") rather than claiming Identity is the
  // start point — that was the pre-fix bug at this exact line.
  if (!weakest) {
    return pickVariant(VERDICT_GAPS_NO_LAGGARD, seed, 'verdict.gaps.noLaggard')
  }
  const pillarShort = PILLAR_LABEL_SHORT[weakest.pillar]
  const tpl = pickVariant(VERDICT_GAPS, seed, 'verdict.gaps')
  return tpl.replace('{pillar}', pillarShort)
}

/* -------------------------------------------------------------------------- */
/* Headline modifier (italic emphasis word — kept as single-word table)       */
/* -------------------------------------------------------------------------- */

/**
 * Delta-aware modifier. Each band reads one of three words depending on the
 * shared trend bucket — so a 65-score tenant that's flat reads "mixed"
 * instead of "weakening", and a 90-score tenant that's slipping reads
 * "slipping" instead of "holding". Pre-fix, every 55-69 score read
 * "weakening" regardless of delta, which contradicted TREND_FLAT.
 */
function pickModifier(score: number, bucket: DeltaBucket): string {
  if (score >= 85) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'lifting'
    if (isNegativeBucket(bucket)) return 'slipping'
    return 'holding'
  }
  if (score >= 70) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'strong'
    if (isNegativeBucket(bucket)) return 'slipping'
    return 'mixed'
  }
  if (score >= 55) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'recovering'
    if (isNegativeBucket(bucket)) return 'weakening'
    return 'mixed'
  }
  if (bucket === 'risingFast' || bucket === 'rising') return 'recovering'
  return 'fragile'
}

/* -------------------------------------------------------------------------- */
/* Weak-link sentence (complete sentence, no leading dash)                    */
/* -------------------------------------------------------------------------- */

const WEAKLINK_ONE: readonly string[] = [
  'One soft spot to close.',
  'A single soft spot stands out.',
  'One thing is dragging the score down.',
]

/** Templates take an `{n}` token (word form: "two" / "three"). */
const WEAKLINK_FEW: readonly string[] = [
  '{n} soft spots stand out.',
  '{n} things to clean up.',
  '{n} things are dragging the score down.',
]

/** Templates take a `{pillar}` token (full pillar label). */
const WEAKLINK_DRIFT_PILLAR: readonly string[] = [
  '{pillar} is moving the wrong way.',
  '{pillar} is drifting fast.',
  '{pillar} needs eyes on it this week.',
]

const WEAKLINK_DRIFT_BROAD: readonly string[] = [
  'Rapid drift across the board.',
  'Drift is spreading across pillars.',
  'Posture is moving the wrong way.',
]

/** Templates take a `{pillar}` token (full pillar label). */
const WEAKLINK_LAGGARD: readonly string[] = [
  '{pillar} is the soft spot.',
  '{pillar} is the holdout.',
  '{pillar} is dragging the score down.',
]

const WEAKLINK_TIGHTEN: readonly string[] = [
  'Room to tighten.',
  'Routine cleanup remains.',
  'Hygiene work to round it out.',
]

function buildWeakLink(
  criticalCount: number | undefined,
  weakest: { pillar: Pillar; score: number } | undefined,
  bucket: DeltaBucket,
  seed: string,
): string {
  const numberWord = (n: number): string =>
    ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][n] ?? String(n)

  // Crit-count weak-links retain priority — those are about volume, not lead.
  if (typeof criticalCount === 'number' && criticalCount > 0) {
    if (criticalCount === 1) return pickVariant(WEAKLINK_ONE, seed, 'weakLink.one')
    if (criticalCount <= 3) {
      const tpl = pickVariant(WEAKLINK_FEW, seed, 'weakLink.few')
      return tpl.replace('{n}', numberWord(criticalCount))
    }
    // 4+ critical items. "Moving the wrong way" / "drifting fast" only fires
    // when the trend bucket is actually negative — otherwise we end up
    // pairing "Drift is accelerating" with a high-score / flat-trend brief.
    if (isNegativeBucket(bucket)) {
      if (weakest) {
        const tpl = pickVariant(WEAKLINK_DRIFT_PILLAR, seed, 'weakLink.driftPillar')
        return tpl.replace('{pillar}', PILLAR_LABEL[weakest.pillar])
      }
      return pickVariant(WEAKLINK_DRIFT_BROAD, seed, 'weakLink.driftBroad')
    }
    // 4+ critical but trend is flat or improving — name the laggard if we
    // have one (still useful) and otherwise fall back to the tighten pool.
    if (weakest && weakest.score < 70) {
      const tpl = pickVariant(WEAKLINK_LAGGARD, seed, 'weakLink.laggard')
      return tpl.replace('{pillar}', PILLAR_LABEL[weakest.pillar])
    }
    return pickVariant(WEAKLINK_TIGHTEN, seed, 'weakLink.tighten')
  }

  if (weakest && weakest.score < 70) {
    const tpl = pickVariant(WEAKLINK_LAGGARD, seed, 'weakLink.laggard')
    return tpl.replace('{pillar}', PILLAR_LABEL[weakest.pillar])
  }

  return pickVariant(WEAKLINK_TIGHTEN, seed, 'weakLink.tighten')
}

/* -------------------------------------------------------------------------- */
/* Deck (trend + risk + action, em-dash free)                                 */
/* -------------------------------------------------------------------------- */

const TREND_LIFTING_FAST: readonly string[] = [
  'Drift, framework alignment, and identity hygiene have all moved in the right direction over the past 30 days.',
  'All three pillars improved over the past 30 days.',
  'The score climbed steadily over the past month, lifted by gains across drift, framework, and identity.',
]

const TREND_LIFTING: readonly string[] = [
  'Posture has ticked up over the past 30 days, with steady progress across the three pillars.',
  'Small gains over the past 30 days, spread across the pillars.',
  'The score is up modestly over the past month.',
]

const TREND_FLAT: readonly string[] = [
  'Posture has held flat over the past 30 days. Neither gaining nor giving up ground.',
  "The score hasn't moved over the past 30 days.",
  'No real movement over the past month. The same signals are still doing the work.',
]

const TREND_SLIPPING: readonly string[] = [
  'Posture has slipped slightly over the past 30 days. Nothing alarming, but worth a look.',
  'The score is down a touch over the past month. Worth a look when you can.',
  "Modest decline over the past 30 days. Not yet a trend, but worth watching.",
]

const TREND_FALLING: readonly string[] = [
  'Posture has fallen meaningfully over the past 30 days. Needs eyes on it this week.',
  'The score dropped sharply over the past month.',
  'Significant decline across the past 30 days. This needs a real response, not next sprint.',
]

/** Templates take a `{phrase}` token (already-bolded action title). */
const RISK_TOP_ACTION: readonly string[] = [
  '{phrase} is your biggest move right now. It lifts the score most.',
  '{phrase} is where your time pays back fastest today.',
  '{phrase} is your biggest opportunity to reclaim score.',
]

/** Templates take a `{pillar}` token (already-bolded full pillar label). */
const RISK_PILLAR: readonly string[] = [
  '{pillar} is the soft spot today. Lifting it is where your time pays back fastest.',
  '{pillar} is the one slowing you down. Lifting it moves the score most.',
  '{pillar} is dragging the score down right now.',
]

const RISK_HYGIENE: readonly string[] = [
  "Nothing is shouting for attention. What's left is hygiene work.",
  'The remaining items are hygiene rather than headline risks.',
  "No single gap is leading the story. It's housekeeping from here.",
]

const RISK_COMPOUND: readonly string[] = [
  'Several smaller gaps add up. Closing the biggest lifts the score fastest.',
  'Several smaller things stack up. Tackling the biggest lifts the score fastest.',
  'A handful of smaller gaps add up. The biggest of them is the place to start.',
]

const ACTION_HOLD: readonly string[] = [
  "Keep doing what you're doing. The usual review cadence is enough.",
  'Stay the course this week.',
  'Keep the usual review rhythm.',
]

const ACTION_LEAD: readonly string[] = [
  'Take the lead item this week to keep momentum.',
  'Take the lead item this week.',
  'Knock out the lead item this week to maintain momentum.',
]

const ACTION_TRIAGE: readonly string[] = [
  'Take the highest-impact items first. The rest can wait until next sprint.',
  'Focus on the highest-impact items first; the rest holds until next sprint.',
  'Take the high-impact items first and let the rest wait.',
]

const ACTION_PUSH: readonly string[] = [
  'This one needs a real week of focus, not the usual cadence.',
  'Plan a focused remediation push this sprint.',
  'This is a remediation sprint, not the usual cadence.',
]

/**
 * Risk-slot precedence (highest to lowest):
 *   1. topAction — when the priority-actions ranker has a #1 item, lead with
 *      its title.
 *   2. Pillar-laggard — when one pillar is < 70 and stands out.
 *   3. Hygiene (score >= 85) or Compound (everything else).
 */
function buildDeck(
  score: number,
  bucket: DeltaBucket,
  criticalCount: number,
  weakest: { pillar: Pillar; score: number } | undefined,
  topAction: PostureBriefInput['topAction'] | undefined,
  seed: string,
): string {
  const trend = (() => {
    if (bucket === 'risingFast') return pickVariant(TREND_LIFTING_FAST, seed, 'deck.trend.liftingFast')
    if (bucket === 'rising') return pickVariant(TREND_LIFTING, seed, 'deck.trend.lifting')
    if (bucket === 'flat') return pickVariant(TREND_FLAT, seed, 'deck.trend.flat')
    if (bucket === 'slipping') return pickVariant(TREND_SLIPPING, seed, 'deck.trend.slipping')
    return pickVariant(TREND_FALLING, seed, 'deck.trend.falling')
  })()

  const risk = (() => {
    if (topAction?.title) {
      const phrase = `**${topAction.title.replace(/[.,;:!?]+$/, '')}**`
      const tpl = pickVariant(RISK_TOP_ACTION, seed, 'deck.risk.topAction')
      return tpl.replace('{phrase}', phrase)
    }
    if (weakest && weakest.score < 70) {
      const pillar = `**${PILLAR_LABEL[weakest.pillar]}**`
      const tpl = pickVariant(RISK_PILLAR, seed, 'deck.risk.pillar')
      return tpl.replace('{pillar}', pillar)
    }
    if (score >= 85) return pickVariant(RISK_HYGIENE, seed, 'deck.risk.hygiene')
    return pickVariant(RISK_COMPOUND, seed, 'deck.risk.compound')
  })()

  // Action routing now consults the trend bucket first. Pre-fix the `score
  // >= 90 → HOLD` rule paired "Keep doing what you're doing" with a deck
  // trend that already said the score had dropped meaningfully.
  const action = (() => {
    if (bucket === 'falling') return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
    if (bucket === 'slipping') return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    const calm = score >= 90 && isNonNegativeBucket(bucket) && criticalCount <= 3
    if (calm) return pickVariant(ACTION_HOLD, seed, 'deck.action.hold')
    if (score >= 75) return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    if (score >= 60) return pickVariant(ACTION_TRIAGE, seed, 'deck.action.triage')
    return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
  })()

  return `${trend} ${risk} ${action}`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a PostureBrief from the active posture state. All inputs are
 * optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the page mounted before the
 * score query resolved) we return a neutral "reading" brief rather than
 * synthesizing a worst-band "fragile / multiple gaps" verdict. Without this
 * guard the composer would coerce `null → 0`, which lands the loading state
 * in the lowest editorial band and shows the user an authoritative-but-wrong
 * verdict for a fraction of a second before the real data arrives.
 *
 * Note: `score === 0` is a legitimate real value (e.g. a brand-new tenant
 * with no MFA adoption) and still routes through the normal band logic —
 * only `null`/`undefined` triggers the loading brief.
 */
export function composePostureBrief(input: PostureBriefInput = {}): PostureBrief {
  const userEmail = input.userEmail ?? 'you'
  const now = input.asOfDate ?? new Date()
  const deltaValue = typeof input.deltas?.score === 'number' ? input.deltas.score : 0
  const deltaDays = typeof input.deltas?.days === 'number' ? input.deltas.days : 30

  const byline =
    userEmail === 'you'
      ? 'Brief by **Guardian Continuous Assessment**'
      : `Brief by **Guardian Continuous Assessment** · For **${userEmail}**`

  if (input.score == null) {
    return {
      kicker: formatKicker(now),
      headlineSubject: 'Posture is',
      headlineModifier: 'loading',
      weakLink: 'Reading the latest signal.',
      deck: 'Pulling the latest posture data. The brief updates once the scan resolves.',
      verdict: 'Reading posture…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
    }
  }

  const score = input.score
  const bucket = bucketDelta(deltaValue)

  const pillars = normalizePillars(input.pillarScores)
  const weakest = weakestPillar(pillars)

  // Daily seed: stable per (tenant, local day). Tenants share a seed when
  // unset (still deterministic for tests + anonymous flows).
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}`

  const headlineModifier = pickModifier(score, bucket)
  const weakLink = buildWeakLink(input.criticalCount, weakest, bucket, seed)
  const verdict = pickVerdict(score, bucket, weakest, input.topAction, seed)
  const deck = buildDeck(score, bucket, input.criticalCount ?? 0, weakest, input.topAction, seed)

  return {
    kicker: formatKicker(now),
    headlineSubject: 'Posture is',
    headlineModifier,
    weakLink,
    deck,
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Variant tables exposed for shape-assertions in tests. Not part of the
 *  public runtime API; do not import from app code. */
export const __TEST_ONLY__ = {
  VERDICT_EXCELLENT,
  VERDICT_STRONG_NO_LAGGARD,
  VERDICT_STRONG_LAGGARD,
  VERDICT_HIGH_SLIPPING,
  VERDICT_HIGH_SLIPPING_LAGGARD,
  VERDICT_MIXED,
  VERDICT_GAPS,
  VERDICT_GAPS_NO_LAGGARD,
  WEAKLINK_ONE,
  WEAKLINK_FEW,
  WEAKLINK_DRIFT_PILLAR,
  WEAKLINK_DRIFT_BROAD,
  WEAKLINK_LAGGARD,
  WEAKLINK_TIGHTEN,
  PILLAR_LABEL,
  PILLAR_LABEL_SHORT,
}
